// ════════════════════════════════════════════════════════════════════════════
// Interaction Event Types (TODO-065: Patient Interactions Dashboard)
// Дата: 13.02.2026
// ════════════════════════════════════════════════════════════════════════════

export type InteractionEventType =
  | 'appointment_confirm'
  | 'appointment_cancel'
  | 'appointment_reschedule'
  | 'feedback_positive'
  | 'feedback_negative'
  | 'feedback_neutral'
  | 'feedback_review_open'
  | 'care_followup_open'
  | 'promo_open'
  | 'personal_account_open'
  | 'contact_navigation_open'
  | 'gift_choice'
  | 'offer_claim'
  | 'book_from_landing'
  | 'cluster_response'
  | 'custom';

export type InteractionEventStatus = 'new' | 'processed' | 'synced_to_crm';

export interface InteractionEvent {
  id: string;
  tenant_id: string;
  phone: string;
  phone_normalized: string;
  patient_name: string;
  event_type: InteractionEventType;
  callback_data: string;
  button_label: string;
  source_channel: string;
  appointment_id?: string;
  template_id?: string;
  cluster_id?: string;
  message_id?: string;
  original_message_id?: string;
  family_head_phone?: string;
  metadata?: Record<string, unknown>;
  status: InteractionEventStatus;
  webhook_sent: boolean;
  webhook_sent_at?: string;
  created_at: string;
  updated_at: string;
}

export interface InteractionEventSummary {
  event_type: InteractionEventType;
  count: number;
  date: string; // YYYY-MM-DD
}

export interface InteractionListResponse {
  events: InteractionEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface InteractionSummaryResponse {
  summary: InteractionEventSummary[];
  date_from: string;
  date_to: string;
}

export interface InteractionFilters {
  tenant_id: string;
  event_type?: InteractionEventType;
  status?: InteractionEventStatus; // Зарезервировано: board/table dashboard пока не отправляет этот фильтр в API
  phone?: string;
  search?: string;
  channel?: string; // Зарезервировано: пригодится для отдельного channel-aware interactions фильтра
  scenario_kind?: string;
  interaction_intent?: string;
  date_from?: string;
  date_to?: string;
  has_appointment?: boolean;
  limit?: number;
  offset?: number;
}

// Human-readable labels for event types
export const EVENT_TYPE_LABELS: Record<InteractionEventType, string> = {
  appointment_confirm: 'Подтверждено',
  appointment_cancel: 'Отменено',
  appointment_reschedule: 'Перенос',
  feedback_positive: 'Отлично',
  feedback_negative: 'Плохо',
  feedback_neutral: 'Нормально',
  feedback_review_open: 'Переход к отзыву',
  care_followup_open: 'Открыты рекомендации',
  promo_open: 'Открыт оффер',
  personal_account_open: 'Открыт личный кабинет',
  contact_navigation_open: 'Открыты каналы связи',
  gift_choice: 'Выбор подарка',
  offer_claim: 'Получение оффера',
  book_from_landing: 'Переход к записи',
  cluster_response: 'Ответ (кластер)',
  custom: 'Прочее',
};

// Colors for event types (Tailwind CSS)
export const EVENT_TYPE_COLORS: Record<InteractionEventType, string> = {
  appointment_confirm: 'bg-green-100 text-green-800',
  appointment_cancel: 'bg-red-100 text-red-800',
  appointment_reschedule: 'bg-yellow-100 text-yellow-800',
  feedback_positive: 'bg-emerald-100 text-emerald-800',
  feedback_negative: 'bg-rose-100 text-rose-800',
  feedback_neutral: 'bg-gray-100 text-gray-800',
  feedback_review_open: 'bg-cyan-100 text-cyan-800',
  care_followup_open: 'bg-blue-100 text-blue-800',
  promo_open: 'bg-fuchsia-100 text-fuchsia-800',
  personal_account_open: 'bg-violet-100 text-violet-800',
  contact_navigation_open: 'bg-sky-100 text-sky-800',
  gift_choice: 'bg-pink-100 text-pink-800',
  offer_claim: 'bg-orange-100 text-orange-800',
  book_from_landing: 'bg-amber-100 text-amber-800',
  cluster_response: 'bg-blue-100 text-blue-800',
  custom: 'bg-purple-100 text-purple-800',
};

// Channel labels
export const CHANNEL_LABELS: Record<string, string> = {
  telegram_bot: 'TG Bot',
  whatsapp_waba: 'WABA',
  whatsapp_regular: 'WA Regular',
  sms: 'SMS',
  sms_tel: 'SMS-Tel',
  sms_shortlink: 'SMS Shortlink',
  whatsapp_regular_shortlink: 'WA Regular Shortlink',
  whatsapp_waba_shortlink: 'WABA Shortlink',
  telegram_bot_shortlink: 'TG Bot Shortlink',
  telegram_client_shortlink: 'TG Client Shortlink',
  max: 'MAX',
  max_client: 'Max Cli',
  max_shortlink: 'MAX Shortlink',
  max_client_shortlink: 'MAX Client Shortlink',
};

// Channel icons (emoji for simplicity)
export const CHANNEL_ICONS: Record<string, string> = {
  telegram_bot: '🤖',
  whatsapp_waba: '💬',
  whatsapp_regular: '📱',
  sms: '📩',
  sms_tel: '📲',
  sms_shortlink: '🔗',
  whatsapp_regular_shortlink: '🔗',
  whatsapp_waba_shortlink: '🔗',
  telegram_bot_shortlink: '🔗',
  telegram_client_shortlink: '🔗',
  max: '🔵',
  max_shortlink: '🔗',
  max_client: '💜',
  max_client_shortlink: '🔗',
};
